<template>
  <section class="min-h-[80vh] flex items-center justify-center py-10 px-4 sm:px-6">
    <div 
      class="w-full max-w-2xl bg-[#fff8f1] rounded-2xl border border-blog-gold shadow-xl p-6 sm:p-10 text-left relative overflow-hidden transition-all"
      :class="classToUse"
    >
      <div class="flex justify-between items-center pb-6 border-b border-blog-gold">
        <img src="@/assets/mp-logo.webp" class="w-32 object-contain" alt="Mercado Pago">
        <span 
          v-if="orderDataObtained && statusTheme"
          class="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border shadow-sm"
          :class="statusTheme.badgeClass"
        >
          <i :class="statusTheme.icon" class="text-[10px]"></i>
          {{ translate(type, 'upper') }}
        </span>
      </div>

      <div v-if="!orderDataObtained" class="py-16 flex flex-col items-center justify-center">
        <LoaderComponent />
      </div>

      <div v-else class="mt-6 text-secondary">
        
        <div class="flex items-start gap-4 mb-6">
          <div 
            class="hidden sm:flex items-center justify-center w-12 h-12 rounded-full shrink-0 mt-1"
            :class="statusTheme.iconBg"
          >
            <i :class="[statusTheme.icon, statusTheme.iconColor]" class="text-xl"></i>
          </div>
          <div>
            <h1 class="f-font-times-new-roman text-2xl sm:text-3xl font-bold text-blog-old-red tracking-tight">
              {{ getTitle() }}
            </h1>
            <p class="text-sm text-gray-600 mt-2 leading-relaxed">
              {{ getMessage() }}
            </p>
          </div>
        </div>

        <div v-if="orderData" class="mt-8 pt-6 border-t border-blog-gold space-y-6">
          
          <div class="flex justify-between items-baseline bg-white p-4 rounded-xl border border-blog-gold">
            <span class="text-xs font-bold uppercase tracking-widest text-blog-red">
              {{ translate('total', 'ucfirst') }}:
            </span>
            <span class="f-font-times-new-roman font-bold text-2xl sm:text-3xl text-blog-old-red font-mono">
              {{ orderData.total_paid ?? orderData.preference_data?.formatedPrice ?? 'R$ 0.00' }}
            </span>
          </div>

          <div class="space-y-3">
            <p class="text-xs font-bold text-blog-red uppercase tracking-widest">
              {{ translate('Purchase information:') }}
            </p>
            <div class="bg-white rounded-xl border border-blog-gold p-4 space-y-2.5">
              <div 
                v-for="(item, index) of orderData.items" 
                :key="index" 
                class="flex items-center text-sm text-gray-800 font-medium py-1 border-b border-gray-100 last:border-none"
              >
                <i class="pi pi-sparkles text-blog-red text-xs mr-2.5 opacity-70"></i>
                <span>{{ item.title }}</span>
              </div>
            </div>
          </div>

          <div v-if="orderData.payment_id" class="flex items-center justify-between text-xs text-gray-600 pt-2">
            <span class="uppercase tracking-wider font-semibold text-gray-400">
              {{ translate('Transaction number:') }}
            </span>
            <span class="font-mono font-bold text-blog-old-red bg-white/80 px-2.5 py-1 rounded border border-blog-gold/20">
              {{ orderData.payment_id }}
            </span>
          </div>

          <div class="mt-8 pt-4 border-t border-blog-gold">

            <div v-if="orderData.payment_id && paymentApproved()" class="flex flex-wrap justify-center md:justify-end gap-3">
              <Button 
                :label="translate('VIEW RECEIPT', 'upper')" 
                icon="pi pi-receipt" 
                @click="openReceiptOnMercadoPago()" 
                :loading="exporting"
                class="bg-button bg-blog-red hover:bg-blog-old-red text-white px-6 py-2.5 text-sm font-semibold rounded-full shadow-md transition border-none cursor-pointer w-full" 
              />
              <Button 
                :label="translate('see item', 'upper')" 
                icon="pi pi-angle-right" 
                @click="goToItemPage()" 
                class="no-bg-button bg-blog-red hover:bg-blog-old-red text-white px-6 py-2.5 text-sm font-semibold rounded-full shadow-md transition border-none cursor-pointer w-full" 
              />
            </div>

            <div v-if="!paymentApproved()" class="text-left bg-amber-50/60 border border-amber-200/60 rounded-xl p-4">
              <p class="text-sm text-amber-900 leading-relaxed" v-html="getPendingMessage()"></p>
            </div>

          </div>
        </div>

      </div>
    </div>
  </section>
</template>

<script setup>
import { isScreeSmall } from '@/composables/windowSize';
import { onUnmounted, onMounted, ref, computed } from 'vue';
import LoaderComponent from '@/components/Utils/LoaderComponent.vue';
import paymentService from '@/services/PaymentService';
import { useRoute } from 'vue-router';
import { translate } from '@/composables/translate';
import urlHandler from '@/composables/urlHandler';
import { showToast } from '@/composables/toast';
import { useToast } from 'primevue';
import Button from 'primevue/button';

const toast = useToast();
const route = useRoute();
const orderDataObtained = ref(false);
const orderData = ref({});
const type = ref('success');
const breakPoint = 500;

onMounted(() => {
  window.addEventListener('resize', updateScreenSize);
  updateScreenSize();
  getOrderData();
});

onUnmounted(() => {
  window.removeEventListener('resize', updateScreenSize);
});

const classToUse = ref('');
const screenSize = ref(window.innerWidth);
const updateScreenSize = () => {
  screenSize.value = window.innerWidth;
  classToUse.value = isScreeSmall(breakPoint) ? 'w-full' : 'm-form-w';
};

/**
 * Mapeamento visual para badges e ícones baseados no tipo do pedido
 */
const statusTheme = computed(() => {
  switch (type.value) {
    case 'approved':
      return {
        badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-600',
        icon: 'pi pi-check-circle'
      };
    case 'created':
      return {
        badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
        icon: 'pi pi-clock'
      };
    case 'refused':
      return {
        badgeClass: 'bg-rose-50 text-rose-700 border-rose-200',
        iconBg: 'bg-rose-100',
        iconColor: 'text-rose-600',
        icon: 'pi pi-times-circle'
      };
    case 'expired':
      return {
        badgeClass: 'bg-gray-100 text-gray-700 border-gray-300',
        iconBg: 'bg-gray-200',
        iconColor: 'text-gray-600',
        icon: 'pi pi-exclamation-circle'
      };
    default:
      return {
        badgeClass: 'bg-gray-100 text-gray-700 border-gray-300',
        iconBg: 'bg-gray-100',
        iconColor: 'text-gray-500',
        icon: 'pi pi-info-circle'
      };
  }
});

const messages = {
  approved: {
    title: 'Payment successful!',
    message: 'Mercado Pago has processed your payment. Your course will be available in your members area shortly.',
    btn_main: 'VIEW RECEIPT'
  },
  created: {
    title: 'Awaiting payment',
    message: 'Your order has been created and we are awaiting payment confirmation from Mercado Pago.',
    valid: 'You can still complete your payment by clicking the button below:',
    not_valid: 'This payment link is no longer valid.',
    not_valid_2: 'To proceed, you can generate a new payment link:',
    btn_valid: 'PAY NOW',
    btn_not_valid: 'RETRY PAYMENT'
  },
  expired: {
    title: 'Payment expired',
    message: 'The time limit to complete this payment has run out.',
    not_valid: 'We could not verify the payment within the required time limit.',
    not_valid_2: "But don't worry! You can generate a new payment link right now:",
    btn_not_valid: 'GENERATE NEW LINK'
  },
  refused: {
    title: 'Payment declined',
    message: 'There was an issue processing your payment with the card issuer.',
    not_valid: 'The payment was not authorized by Mercado Pago.',
    not_valid_2: 'You can try again by generating a new link:',
    btn_not_valid: 'TRY AGAIN'
  },
  no_status: {
    title: 'Unknown status',
    message: 'We could not determine the current status of this order. Please check your email or contact support.'
  }
};

function getTitle() {
  return translate(messages[type.value]?.title || messages.no_status.title);
}

function getMessage() {
  return translate(messages[type.value]?.message || messages.no_status.message);
}

function getPendingMessage() {
  const msgSource = messages[type.value] || messages.created;
  if (orderData.value.valid_link) {
    return translate(msgSource.valid || messages.created.valid);
  } 

  const part1 = translate(msgSource.not_valid || messages.created.not_valid);
  const part2 = translate(msgSource.not_valid_2 || messages.created.not_valid_2);
  return `${part1} <br> ${part2}`;
}

async function getOrderData() {
  const routeParams = route.query;
  let externalReference = routeParams.payment_id ?? '';
  
  let payment = null;
  if (externalReference) {
    payment = await paymentService.getPayment(externalReference);
  }

  orderData.value = payment;
  type.value = payment ? payment.mapStatus() : 'no_status';
  orderDataObtained.value = true;
}

function openReceiptOnMercadoPago() {
  paymentService.openMercadoPagoReceiptPage(orderData.value.payment_id);
}

function paymentApproved() {
  return ['approved'].includes(type.value);
}

function goToCoursePage(courseUuid = null) {
    if (courseUuid) {
        urlHandler.redirectAndScroll(`/course/${courseUuid}`);
    } else {
        showToast(toast, translate('course not found', 'ucfirst'), 'info');
    }
}

function goToSellingMaterialGroupPage(sellingMaterialGroupId = null) {
    if (sellingMaterialGroupId) {
        urlHandler.redirectAndScroll(`/dashboard/teacher_materials`, {selected: sellingMaterialGroupId});
    } else {
        showToast(toast, translate('material not found', 'ucfirst'), 'info');
    }
}

function goToItemPage() {
    const item = orderData.value.items[0] || null;

    console.log('or', orderData)

    switch (orderData.value.product_type) {
        case 'course':
          goToCoursePage(item.uuid);
        break;
        case 'sellingmaterialgroup':
          goToSellingMaterialGroupPage(item.id);
        break;
    }
}
</script>